<template>
	<div class="tiptap-editor" :class="{ 'tiptap-fullscreen': isFullscreen }" @click.self="closeAllDropdowns">
		<div v-if="editor" class="tiptap-toolbar">
			<div class="tiptap-toolbar-group">
				<button type="button" class="tiptap-btn" title="Deshacer" :disabled="!editor.can().undo()" @click="editor.chain().focus().undo().run()">
					<span class="tiptap-icon">&#x21A9;</span>
				</button>
				<button type="button" class="tiptap-btn" title="Rehacer" :disabled="!editor.can().redo()" @click="editor.chain().focus().redo().run()">
					<span class="tiptap-icon">&#x21AA;</span>
				</button>
			</div>

			<span class="tiptap-separator" />

			<div class="tiptap-toolbar-group">
				<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('bold') }" title="Negrita" @click="editor.chain().focus().toggleBold().run()">
					<strong>B</strong>
				</button>
				<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('italic') }" title="Cursiva" @click="editor.chain().focus().toggleItalic().run()">
					<em>I</em>
				</button>
				<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('underline') }" title="Subrayado" @click="editor.chain().focus().toggleUnderline().run()">
					<u>U</u>
				</button>
				<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('strike') }" title="Tachado" @click="editor.chain().focus().toggleStrike().run()">
					<s>S</s>
				</button>
			</div>

			<span class="tiptap-separator" />

			<div class="tiptap-toolbar-group">
				<select class="tiptap-select" @change="setHeading($event)">
					<option value="p" :selected="editor.isActive('paragraph')">
						Párrafo
					</option>
					<option value="2" :selected="editor.isActive('heading', { level: 2 })">
						Título 2
					</option>
					<option value="3" :selected="editor.isActive('heading', { level: 3 })">
						Título 3
					</option>
					<option value="4" :selected="editor.isActive('heading', { level: 4 })">
						Título 4
					</option>
					<option value="5" :selected="editor.isActive('heading', { level: 5 })">
						Título 5
					</option>
					<option value="6" :selected="editor.isActive('heading', { level: 6 })">
						Título 6
					</option>
				</select>
			</div>

			<span class="tiptap-separator" />

			<div class="tiptap-toolbar-group">
				<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }" title="Alinear izquierda" @click="editor.chain().focus().setTextAlign('left').run()">
					&#x2630;
				</button>
				<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }" title="Centrar" @click="editor.chain().focus().setTextAlign('center').run()">
					&#x2632;
				</button>
				<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive({ textAlign: 'right' }) }" title="Alinear derecha" @click="editor.chain().focus().setTextAlign('right').run()">
					&#x2631;
				</button>
				<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive({ textAlign: 'justify' }) }" title="Justificar" @click="editor.chain().focus().setTextAlign('justify').run()">
					&#x2263;
				</button>
			</div>

			<span class="tiptap-separator" />

			<div class="tiptap-toolbar-group">
				<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('bulletList') }" title="Lista" @click="editor.chain().focus().toggleBulletList().run()">
					&#8226; Lista
				</button>
				<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('orderedList') }" title="Lista numerada" @click="editor.chain().focus().toggleOrderedList().run()">
					1. Lista
				</button>
				<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('taskList') }" title="Lista de tareas" @click="editor.chain().focus().toggleTaskList().run()">
					&#x2611; Tareas
				</button>
			</div>

			<span class="tiptap-separator" />

			<div class="tiptap-toolbar-group">
				<input type="color" class="tiptap-color-input" title="Color de texto" :value="editor.getAttributes('textStyle').color || '#000000'" @input="editor.chain().focus().setColor($event.target.value).run()">
				<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('highlight') }" title="Resaltar" @click="editor.chain().focus().toggleHighlight().run()">
					<span style="background: yellow; padding: 0 3px;">A</span>
				</button>
				<button type="button" class="tiptap-btn" title="Limpiar formato" @click="editor.chain().focus().clearNodes().unsetAllMarks().run()">
					&#x2715;
				</button>
				<button type="button" class="tiptap-btn" :class="{ 'is-active': !!painter }" title="Copiar formato: pon el cursor en el texto con el formato deseado, pulsa aquí y luego selecciona el texto destino" @click="captureFormat">
					&#x1F58C;
				</button>
			</div>

			<span class="tiptap-separator" />

			<div class="tiptap-toolbar-group">
				<button type="button" class="tiptap-btn" title="Imagen por URL" @click="addImage">
					&#x1F5BC;
				</button>
				<button v-if="fileUploadHandler" type="button" class="tiptap-btn" title="Subir imagen desde el ordenador (o arrastra/pega una imagen)" @click="$refs.fileInput.click()">
					&#x2B06;
				</button>
				<input ref="fileInput" type="file" accept="image/*" multiple style="display: none;" @change="onFilePicked">
				<button v-if="speechSupported" type="button" class="tiptap-btn" :class="{ 'is-active': listening }" :title="listening ? 'Parar dictado por voz' : 'Dictar por voz'" @click="toggleDictation">
					&#x1F3A4;
				</button>
				<button type="button" class="tiptap-btn" title="Video YouTube" @click="addYoutube">
					&#x25B6;
				</button>
				<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('link') }" title="Enlace" @click="setLink">
					&#x1F517;
				</button>
				<button v-if="editor.isActive('link')" type="button" class="tiptap-btn" title="Quitar enlace" @click="editor.chain().focus().unsetLink().run()">
					&#x26D3;&#xFE0E;&#x2715;
				</button>
			</div>

			<span class="tiptap-separator" />

			<!-- Emoji picker -->
			<div class="tiptap-toolbar-group">
				<div class="tiptap-dropdown">
					<button type="button" class="tiptap-btn" title="Emojis" @click.stop="toggleEmojiPicker">
						&#x1F600;
					</button>
					<div v-if="showEmojiPicker" class="tiptap-dropdown-menu tiptap-emoji-menu" @click.stop>
						<div v-for="group in emojiGroups" :key="group.label" class="tiptap-emoji-group">
							<div class="tiptap-emoji-group-label">
								{{ group.label }}
							</div>
							<div class="tiptap-emoji-grid">
								<button
									v-for="emoji in group.emojis"
									:key="emoji"
									type="button"
									class="tiptap-emoji-btn"
									:title="emoji"
									@click="insertEmoji(emoji)"
								>
									{{ emoji }}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Special characters -->
			<div class="tiptap-toolbar-group">
				<div class="tiptap-dropdown">
					<button type="button" class="tiptap-btn" title="Caracteres especiales" @click.stop="toggleCharMap">
						&#x03A9;
					</button>
					<div v-if="showCharMap" class="tiptap-dropdown-menu tiptap-charmap-menu" @click.stop>
						<div v-for="group in charGroups" :key="group.label" class="tiptap-emoji-group">
							<div class="tiptap-emoji-group-label">
								{{ group.label }}
							</div>
							<div class="tiptap-emoji-grid">
								<button
									v-for="char in group.chars"
									:key="char.char"
									type="button"
									class="tiptap-emoji-btn"
									:title="char.name"
									@click="insertChar(char.char)"
								>
									{{ char.char }}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<span class="tiptap-separator" />

			<div class="tiptap-toolbar-group">
				<button type="button" class="tiptap-btn" title="Tabla" @click="editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()">
					&#x2637;
				</button>
			</div>

			<div class="tiptap-toolbar-group">
				<div class="tiptap-dropdown">
					<button type="button" class="tiptap-btn" title="Cambiar mayúsculas/minúsculas" @click.stop="toggleCaseMenu">
						Aa &#x25BE;
					</button>
					<div v-if="showCaseMenu" class="tiptap-dropdown-menu" @click.stop>
						<button type="button" class="tiptap-dropdown-item" @click="changeCase('upper')">
							MAYÚSCULAS
						</button>
						<button type="button" class="tiptap-dropdown-item" @click="changeCase('lower')">
							minúsculas
						</button>
						<button type="button" class="tiptap-dropdown-item" @click="changeCase('title')">
							Cada Palabra
						</button>
						<button type="button" class="tiptap-dropdown-item" @click="changeCase('sentence')">
							Tipo frase
						</button>
					</div>
				</div>
			</div>

			<span v-if="showTemplates" class="tiptap-separator" />

			<div v-if="showTemplates" class="tiptap-toolbar-group">
				<div class="tiptap-dropdown">
					<button type="button" class="tiptap-btn" @click.stop="showTemplateMenu = !showTemplateMenu">
						Plantillas &#x25BE;
					</button>
					<div v-if="showTemplateMenu" class="tiptap-dropdown-menu" @click.stop>
						<button type="button" class="tiptap-dropdown-item" @click="insertTemplate('coupons')">
							Cupones
						</button>
						<button type="button" class="tiptap-dropdown-item" @click="insertTemplate('faq')">
							FAQ
						</button>
						<button type="button" class="tiptap-dropdown-item" @click="insertTemplate('payments')">
							Métodos de pago
						</button>
					</div>
				</div>
			</div>

			<div v-if="generateHandler" class="tiptap-toolbar-group">
				<button type="button" class="tiptap-btn tiptap-btn-generate" :disabled="generating" title="Generar descripción automáticamente" @click="runGenerate">
					<span v-if="generating">&#x231B; Generando&#8230;</span>
					<span v-else>&#x2728; Generar descripción</span>
				</button>
			</div>

			<div class="tiptap-toolbar-right">
				<button type="button" class="tiptap-btn" title="Pantalla completa" @click="isFullscreen = !isFullscreen">
					&#x26F6;
				</button>
			</div>
		</div>

		<bubble-menu v-if="editor" :editor="editor" :tippy-options="{ duration: 100 }" class="tiptap-bubble">
			<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('bold') }" title="Negrita" @click="editor.chain().focus().toggleBold().run()">
				<strong>B</strong>
			</button>
			<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('italic') }" title="Cursiva" @click="editor.chain().focus().toggleItalic().run()">
				<em>I</em>
			</button>
			<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('underline') }" title="Subrayado" @click="editor.chain().focus().toggleUnderline().run()">
				<u>U</u>
			</button>
			<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('strike') }" title="Tachado" @click="editor.chain().focus().toggleStrike().run()">
				<s>S</s>
			</button>
			<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('highlight') }" title="Resaltar" @click="editor.chain().focus().toggleHighlight().run()">
				<span style="background: yellow; padding: 0 3px;">A</span>
			</button>
			<button type="button" class="tiptap-btn" :class="{ 'is-active': editor.isActive('link') }" title="Enlace" @click="setLink">
				&#x1F517;
			</button>
		</bubble-menu>

		<div v-if="slash.open" class="tiptap-slash-menu" :style="{ top: `${slash.top}px`, left: `${slash.left}px` }" @mousedown.prevent>
			<button
				v-for="(cmd, i) in filteredSlashCommands"
				:key="cmd.label"
				type="button"
				class="tiptap-slash-item"
				:class="{ 'is-active': i === slash.index }"
				@click="applySlash(cmd)"
			>
				<span class="tiptap-slash-icon">{{ cmd.icon }}</span> {{ cmd.label }}
			</button>
			<div v-if="!filteredSlashCommands.length" class="tiptap-slash-empty">
				Sin comandos
			</div>
		</div>

		<editor-content :editor="editor" class="tiptap-content" :style="{ height: contentHeight }" />

		<div v-if="editor" class="tiptap-statusbar">
			<span>{{ editor.storage.characterCount.characters() }} caracteres</span>
			<span class="ml-3">{{ editor.storage.characterCount.words() }} palabras</span>
		</div>
	</div>
</template>

<script>
import { markRaw } from 'vue';
import { Editor, EditorContent, BubbleMenu } from '@tiptap/vue-3';
import { TextSelection } from '@tiptap/pm/state';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import CharacterCount from '@tiptap/extension-character-count';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import Youtube from '@tiptap/extension-youtube';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';

export default {
	name: 'TiptapEditor',
	components: {
		EditorContent,
		BubbleMenu,
	},
	props: {
		modelValue: {
			type: String,
			default: '',
		},
		height: {
			type: Number,
			default: 400,
		},
		showTemplates: {
			type: Boolean,
			default: false,
		},
		uploadHandler: {
			type: Function,
			default: null,
		},
		generateHandler: {
			type: Function,
			default: null,
		},
		fileUploadHandler: {
			type: Function,
			default: null,
		},
	},
	emits: ['update:modelValue'],
	data() {
		return {
			editor: null,
			isFullscreen: false,
			showTemplateMenu: false,
			showEmojiPicker: false,
			showCharMap: false,
			showCaseMenu: false,
			generating: false,
			painter: null,
			listening: false,
			recognition: null,
			slash: {
				open: false,
				query: '',
				top: 0,
				left: 0,
				index: 0,
			},
			emojiGroups: [
				{
					label: 'Caras',
					emojis: ['😀', '😁', '😂', '🤣', '😃', '😄', '😅', '😆', '😉', '😊', '😋', '😎', '😍', '😘', '🥰', '😗', '😙', '😚', '🙂', '🤗', '🤩', '🤔', '🤨', '😐', '😑', '😶', '🙄', '😏', '😣', '😥', '😮', '🤐', '😯', '😪', '😫', '🥱', '😴', '😌', '😛', '😜', '😝', '🤤', '😒', '😓', '😔', '😕', '🙃', '🤑', '😲', '🙁', '😖', '😞', '😟', '😤', '😢', '😭', '😦', '😧', '😨', '😩', '🤯', '😬', '😰', '😱', '🥵', '🥶', '😳', '🤪', '😵', '🤭', '🤫', '🤥', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥴', '😇', '🤠', '🥳'],
				},
				{
					label: 'Gestos',
					emojis: ['👍', '👎', '👌', '🤌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👋', '🤚', '🖐', '✋', '🖖', '💪', '🦾', '🤲', '🙌', '👏', '🤝', '🙏'],
				},
				{
					label: 'Corazones',
					emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️', '✝️', '🔥', '⭐', '🌟', '💫', '✨', '🎉', '🎊'],
				},
				{
					label: 'Naturaleza',
					emojis: ['🌸', '🌺', '🌻', '🌹', '🌷', '🌼', '💐', '🍀', '🌿', '🍃', '🌱', '🌲', '🌳', '🌴', '🍄', '🌾', '🌵', '🎋', '🎍', '🌾', '🍁', '🍂', '🍃'],
				},
				{
					label: 'Comida',
					emojis: ['🍕', '🍔', '🌮', '🌯', '🥙', '🧆', '🥚', '🍳', '🥘', '🍲', '🥗', '🥣', '🍱', '🍛', '🍜', '🍝', '🍠', '🍢', '🍣', '🍤', '🍙', '🍚', '🍘', '🍥', '🥮', '🍡', '🧁', '🍰', '🎂', '🍮', '🍭', '🍬', '🍫', '🍿', '🍩', '🍪', '🌰', '🥜', '🍯'],
				},
				{
					label: 'Símbolos',
					emojis: ['✅', '❌', '⚠️', 'ℹ️', '🔴', '🟡', '🟢', '🔵', '⚫', '⚪', '🔶', '🔷', '🔸', '🔹', '🔺', '🔻', '💠', '🔘', '🔳', '🔲', '▪️', '▫️', '◾', '◽', '◼️', '◻️', '🔱', '⚜️', '🏁', '🚩', '🎌', '🏴', '🏳️', '📌', '📍', '🗺️', '🧭'],
				},
			],
			charGroups: [
				{
					label: 'Monedas',
					chars: [
						{ char: '€', name: 'Euro' },
						{ char: '$', name: 'Dólar' },
						{ char: '£', name: 'Libra' },
						{ char: '¥', name: 'Yen' },
						{ char: '₽', name: 'Rublo' },
						{ char: '₿', name: 'Bitcoin' },
						{ char: '¢', name: 'Centavo' },
						{ char: '₩', name: 'Won' },
					],
				},
				{
					label: 'Legal',
					chars: [
						{ char: '©', name: 'Copyright' },
						{ char: '®', name: 'Marca registrada' },
						{ char: '™', name: 'Marca comercial' },
						{ char: '℗', name: 'Grabación' },
					],
				},
				{
					label: 'Matemáticas',
					chars: [
						{ char: '±', name: 'Más o menos' },
						{ char: '×', name: 'Multiplicación' },
						{ char: '÷', name: 'División' },
						{ char: '≠', name: 'Distinto' },
						{ char: '≤', name: 'Menor o igual' },
						{ char: '≥', name: 'Mayor o igual' },
						{ char: '≈', name: 'Aproximado' },
						{ char: '∞', name: 'Infinito' },
						{ char: '∑', name: 'Suma' },
						{ char: '√', name: 'Raíz' },
						{ char: 'π', name: 'Pi' },
						{ char: '°', name: 'Grados' },
						{ char: '½', name: 'Mitad' },
						{ char: '¼', name: 'Cuarto' },
						{ char: '¾', name: 'Tres cuartos' },
					],
				},
				{
					label: 'Flechas',
					chars: [
						{ char: '→', name: 'Flecha derecha' },
						{ char: '←', name: 'Flecha izquierda' },
						{ char: '↑', name: 'Flecha arriba' },
						{ char: '↓', name: 'Flecha abajo' },
						{ char: '↔', name: 'Flecha doble' },
						{ char: '↕', name: 'Flecha doble vertical' },
						{ char: '⇒', name: 'Implica' },
						{ char: '⇐', name: 'Implica izquierda' },
						{ char: '⇔', name: 'Equivalencia' },
						{ char: '➜', name: 'Flecha gruesa' },
						{ char: '➡', name: 'Flecha negra derecha' },
					],
				},
				{
					label: 'Puntuación',
					chars: [
						{ char: '…', name: 'Puntos suspensivos' },
						{ char: '–', name: 'Guión largo' },
						{ char: '—', name: 'Raya' },
						{ char: '«', name: 'Comilla francesa izq.' },
						{ char: '»', name: 'Comilla francesa der.' },
						{ char: '‘', name: 'Comilla izquierda' },
						{ char: '’', name: 'Comilla derecha' },
						{ char: '“', name: 'Comilla doble izq.' },
						{ char: '”', name: 'Comilla doble der.' },
						{ char: '¡', name: 'Exclamación inicial' },
						{ char: '¿', name: 'Interrogación inicial' },
						{ char: '·', name: 'Punto medio' },
					],
				},
				{
					label: 'Letras especiales',
					chars: [
						{ char: 'á', name: 'a con acento' },
						{ char: 'é', name: 'e con acento' },
						{ char: 'í', name: 'i con acento' },
						{ char: 'ó', name: 'o con acento' },
						{ char: 'ú', name: 'u con acento' },
						{ char: 'ü', name: 'u con diéresis' },
						{ char: 'ñ', name: 'eñe' },
						{ char: 'à', name: 'a grave' },
						{ char: 'è', name: 'e grave' },
						{ char: 'ì', name: 'i grave' },
						{ char: 'ò', name: 'o grave' },
						{ char: 'ù', name: 'u grave' },
						{ char: 'â', name: 'a circunflejo' },
						{ char: 'ê', name: 'e circunflejo' },
						{ char: 'î', name: 'i circunflejo' },
						{ char: 'ô', name: 'o circunflejo' },
						{ char: 'û', name: 'u circunflejo' },
						{ char: 'ç', name: 'cedilla' },
					],
				},
			],
		};
	},
	computed: {
		contentHeight() {
			return `${this.height - 90}px`;
		},
		speechSupported() {
			return typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
		},
		slashCommands() {
			const e = () => this.editor.chain().focus();
			const cmds = [
				{ label: 'Título 2', icon: 'H2', run: () => e().toggleHeading({ level: 2 }).run() },
				{ label: 'Título 3', icon: 'H3', run: () => e().toggleHeading({ level: 3 }).run() },
				{ label: 'Lista', icon: '•', run: () => e().toggleBulletList().run() },
				{ label: 'Lista numerada', icon: '1.', run: () => e().toggleOrderedList().run() },
				{ label: 'Lista de tareas', icon: '☑', run: () => e().toggleTaskList().run() },
				{ label: 'Cita', icon: '❝', run: () => e().toggleBlockquote().run() },
				{ label: 'Separador', icon: '―', run: () => e().setHorizontalRule().run() },
				{ label: 'Tabla', icon: '⊞', run: () => e().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
				{ label: 'Imagen', icon: '🖼', run: () => { if (this.fileUploadHandler) this.$refs.fileInput.click(); else this.addImage(); } },
				{ label: 'Vídeo YouTube', icon: '▶', run: () => this.addYoutube() },
			];
			if (this.showTemplates) {
				cmds.push({ label: 'Plantilla: Cupones', icon: '🎟', run: () => this.insertTemplate('coupons') });
				cmds.push({ label: 'Plantilla: FAQ', icon: '❓', run: () => this.insertTemplate('faq') });
				cmds.push({ label: 'Plantilla: Métodos de pago', icon: '💳', run: () => this.insertTemplate('payments') });
			}
			if (this.generateHandler) {
				cmds.push({ label: 'Generar descripción', icon: '✨', run: () => this.runGenerate() });
			}
			return cmds;
		},
		filteredSlashCommands() {
			const q = (this.slash.query || '').toLowerCase();
			if (!q) return this.slashCommands;
			return this.slashCommands.filter(c => c.label.toLowerCase().includes(q));
		},
	},
	watch: {
		modelValue(value) {
			if (this.editor && value !== this.editor.getHTML()) {
				this.editor.commands.setContent(value, false);
			}
		},
	},
	mounted() {
		this.editor = new Editor({
			content: this.modelValue,
			extensions: [
				StarterKit.configure({
					heading: {
						levels: [2, 3, 4, 5, 6],
					},
				}),
				Underline,
				TextAlign.configure({
					types: ['heading', 'paragraph'],
				}),
				Link.configure({
					openOnClick: false,
					HTMLAttributes: {
						target: '_blank',
						rel: 'noopener',
					},
				}),
				Image.configure({
					allowBase64: false,
					HTMLAttributes: {
						class: 'tiptap-image',
					},
				}),
				Youtube.configure({
					controls: true,
					nocookie: true,
					HTMLAttributes: {
						class: 'tiptap-youtube',
					},
				}),
				Table.configure({
					resizable: true,
				}),
				TableRow,
				TableCell,
				TableHeader,
				CharacterCount,
				TextStyle,
				Color,
				Highlight.configure({
					multicolor: true,
				}),
				Placeholder.configure({
					placeholder: 'Escribe aquí...',
				}),
				TaskList,
				TaskItem.configure({ nested: true }),
			],
			editorProps: {
				attributes: {
					spellcheck: 'true',
				},
				handleKeyDown: (view, event) => {
					if (event.altKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
						this.moveBlock(event.key === 'ArrowUp' ? 'up' : 'down');
						return true;
					}
					if (!this.slash.open) return false;
					const items = this.filteredSlashCommands;
					if (event.key === 'Escape') { this.closeSlash(); return true; }
					if (!items.length) return false;
					if (event.key === 'ArrowDown') { this.slash.index = (this.slash.index + 1) % items.length; return true; }
					if (event.key === 'ArrowUp') { this.slash.index = (this.slash.index - 1 + items.length) % items.length; return true; }
					if (event.key === 'Enter') { this.applySlash(items[this.slash.index]); return true; }
					return false;
				},
				handleDrop: (view, event) => {
					const files = event.dataTransfer && event.dataTransfer.files;
					if (!files || !files.length) return false;
					const images = Array.from(files).filter(f => f.type.startsWith('image/'));
					if (!images.length) return false;

					event.preventDefault();
					const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
					const pos = coords ? coords.pos : null;
					images.forEach(file => this.uploadAndInsertFile(file, pos));
					return true;
				},
				handlePaste: (view, event) => {
					const files = event.clipboardData && event.clipboardData.files;
					if (files && files.length) {
						const images = Array.from(files).filter(f => f.type.startsWith('image/'));
						if (images.length) {
							event.preventDefault();
							images.forEach(file => this.uploadAndInsertFile(file, null));
							return true;
						}
					}

					const plain = event.clipboardData && event.clipboardData.getData('text/plain');
					if (plain) {
						const url = plain.trim();
						if (/^https?:\/\/\S+$/i.test(url)) {
							const { empty } = view.state.selection;
							if (empty && /(?:youtube\.com|youtu\.be)/i.test(url)) {
								event.preventDefault();
								this.editor.chain().focus().setYoutubeVideo({ src: url }).run();
								return true;
							}
							if (!empty) {
								event.preventDefault();
								this.editor.chain().focus().extendMarkRange('link').setLink({ href: url })
									.run();
								return true;
							}
						}
					}

					const html = event.clipboardData && event.clipboardData.getData('text/html');
					if (!html) return false;

					const isWordExcel = /mso-|urn:schemas-microsoft-com|<!--\[if|MsoNormal/i.test(html);
					if (!isWordExcel) return false;

					const cleaned = this.cleanWordHtml(html);
					this.editor.chain().focus().insertContent(cleaned).run();
					return true;
				},
			},
			onUpdate: () => {
				this.$emit('update:modelValue', this.editor.getHTML());
			},
		});

		this.editor.on('selectionUpdate', this.onSelectionForPainter);
		this.editor.on('update', this.checkSlash);
		this.editor.on('selectionUpdate', this.checkSlash);

		document.addEventListener('click', this.closeAllDropdowns);
	},
	beforeUnmount() {
		if (this.recognition) {
			try { this.recognition.stop(); } catch (e) { /* noop */ }
		}
		if (this.editor) {
			this.editor.destroy();
		}
		document.removeEventListener('click', this.closeAllDropdowns);
	},
	methods: {
		closeAllDropdowns() {
			this.showEmojiPicker = false;
			this.showCharMap = false;
			this.showTemplateMenu = false;
			this.showCaseMenu = false;
			if (this.slash.open) this.closeSlash();
		},
		toggleCaseMenu() {
			this.showEmojiPicker = false;
			this.showCharMap = false;
			this.showTemplateMenu = false;
			this.showCaseMenu = !this.showCaseMenu;
		},
		changeCase(mode) {
			this.showCaseMenu = false;
			const { from, to } = this.editor.state.selection;
			if (from === to) return;
			const text = this.editor.state.doc.textBetween(from, to, ' ');
			if (!text) return;

			let out = text;
			if (mode === 'upper') {
				out = text.toUpperCase();
			} else if (mode === 'lower') {
				out = text.toLowerCase();
			} else if (mode === 'title') {
				out = text.toLowerCase().replace(/\b[\p{L}]/gu, c => c.toUpperCase());
			} else if (mode === 'sentence') {
				const lower = text.toLowerCase();
				out = lower.charAt(0).toUpperCase() + lower.slice(1);
			}

			this.editor.chain().focus().insertContentAt({ from, to }, out).run();
		},
		captureFormat() {
			// Segunda pulsación: desarmar
			if (this.painter) { this.painter = null; return; }
			this.painter = {
				bold: this.editor.isActive('bold'),
				italic: this.editor.isActive('italic'),
				underline: this.editor.isActive('underline'),
				strike: this.editor.isActive('strike'),
				color: this.editor.getAttributes('textStyle').color || null,
				highlight: this.editor.isActive('highlight'),
			};
		},
		onSelectionForPainter() {
			if (!this.painter) return;
			const { from, to } = this.editor.state.selection;
			if (from !== to) this.applyPainter();
		},
		applyPainter() {
			const fmt = this.painter;
			if (!fmt) return;
			const { from, to } = this.editor.state.selection;
			if (from === to) return;
			// Desarmar antes de aplicar para evitar re-disparar selectionUpdate
			this.painter = null;

			let chain = this.editor.chain().focus().setTextSelection({ from, to }).unsetAllMarks();
			if (fmt.bold) chain = chain.setMark('bold');
			if (fmt.italic) chain = chain.setMark('italic');
			if (fmt.underline) chain = chain.setMark('underline');
			if (fmt.strike) chain = chain.setMark('strike');
			if (fmt.color) chain = chain.setColor(fmt.color);
			if (fmt.highlight) chain = chain.setHighlight();
			chain.run();
		},
		async uploadAndInsertFile(file, pos) {
			if (!this.fileUploadHandler) return;
			try {
				const url = await this.fileUploadHandler(file);
				if (!url) return;
				if (typeof pos === 'number') {
					this.editor.chain().focus().insertContentAt(pos, { type: 'image', attrs: { src: url } }).run();
				} else {
					this.editor.chain().focus().setImage({ src: url }).run();
				}
			} catch (e) {
				/* el componente padre gestiona los avisos de error */
			}
		},
		onFilePicked(event) {
			const { files } = event.target;
			if (!files || !files.length) return;
			Array.from(files).forEach(file => this.uploadAndInsertFile(file, null));
			event.target.value = '';
		},
		checkSlash() {
			if (!this.editor) return;
			const { selection } = this.editor.state;
			const { $from, empty } = selection;
			if (!empty || $from.parent.type.name !== 'paragraph') {
				if (this.slash.open) this.closeSlash();
				return;
			}
			const textBefore = $from.parent.textContent.slice(0, $from.parentOffset);
			const match = /^\/([^\s]*)$/.exec(textBefore);
			if (!match) {
				if (this.slash.open) this.closeSlash();
				return;
			}
			const [, query] = match;
			this.slash.query = query;
			this.slash.index = 0;
			const coords = this.editor.view.coordsAtPos(selection.from);
			this.slash.top = coords.bottom + 4;
			this.slash.left = coords.left;
			this.slash.open = true;
		},
		closeSlash() {
			this.slash.open = false;
			this.slash.query = '';
			this.slash.index = 0;
		},
		applySlash(cmd) {
			if (!cmd) return;
			const { from } = this.editor.state.selection;
			const start = from - (this.slash.query.length + 1);
			this.closeSlash();
			this.editor.chain().focus().deleteRange({ from: start, to: from }).run();
			cmd.run();
		},
		// Mueve el bloque de nivel superior actual arriba/abajo (Alt+↑ / Alt+↓), sin dependencias
		moveBlock(direction) {
			const { state, view } = this.editor;
			const { $from } = state.selection;
			if ($from.depth < 1) return;

			const { doc } = state;
			const index = $from.index(0);
			if (direction === 'up' && index === 0) return;
			if (direction === 'down' && index === doc.childCount - 1) return;

			const node = doc.child(index);
			const start = $from.before(1);
			const end = start + node.nodeSize;

			let tr = state.tr.delete(start, end);
			let insertPos;
			if (direction === 'up') {
				insertPos = start - doc.child(index - 1).nodeSize;
			} else {
				insertPos = start + doc.child(index + 1).nodeSize;
			}
			tr = tr.insert(insertPos, node);
			tr = tr.setSelection(TextSelection.near(tr.doc.resolve(insertPos + 1)));
			view.dispatch(tr.scrollIntoView());
			view.focus();
		},
		toggleDictation() {
			const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
			if (!SR) return;

			if (this.listening) {
				if (this.recognition) this.recognition.stop();
				return;
			}

			const recognition = new SR();
			recognition.lang = 'es-ES';
			recognition.continuous = true;
			recognition.interimResults = false;

			recognition.onresult = (event) => {
				let text = '';
				for (let i = event.resultIndex; i < event.results.length; i += 1) {
					if (event.results[i].isFinal) text += event.results[i][0].transcript;
				}
				if (text) this.editor.chain().focus().insertContent(`${text} `).run();
			};
			recognition.onend = () => { this.listening = false; };
			recognition.onerror = () => { this.listening = false; };

			this.recognition = markRaw(recognition);
			recognition.start();
			this.listening = true;
		},
		toggleEmojiPicker() {
			this.showCharMap = false;
			this.showTemplateMenu = false;
			this.showEmojiPicker = !this.showEmojiPicker;
		},
		toggleCharMap() {
			this.showEmojiPicker = false;
			this.showTemplateMenu = false;
			this.showCharMap = !this.showCharMap;
		},
		insertEmoji(emoji) {
			this.editor.chain().focus().insertContent(emoji).run();
			this.showEmojiPicker = false;
		},
		insertChar(char) {
			this.editor.chain().focus().insertContent(char).run();
			this.showCharMap = false;
		},
		addYoutube() {
			const url = window.prompt('URL del vídeo de YouTube:');
			if (url) {
				this.editor.chain().focus().setYoutubeVideo({ src: url }).run();
			}
		},
		setHeading(event) {
			const { value } = event.target;
			if (value === 'p') {
				this.editor.chain().focus().setParagraph().run();
			} else {
				this.editor.chain().focus().toggleHeading({ level: parseInt(value, 10) }).run();
			}
		},
		addImage() {
			const url = window.prompt('URL de la imagen:');
			if (url) {
				if (this.uploadHandler) {
					this.uploadHandler(url).then((uploadedUrl) => {
						if (uploadedUrl) {
							this.editor.chain().focus().setImage({ src: uploadedUrl }).run();
						}
					}).catch(() => {
						this.editor.chain().focus().setImage({ src: url }).run();
					});
				} else {
					this.editor.chain().focus().setImage({ src: url }).run();
				}
			}
		},
		setLink() {
			const previousUrl = this.editor.getAttributes('link').href;
			const url = window.prompt('URL del enlace:', previousUrl);

			if (url === null) return;

			if (url === '') {
				this.editor.chain().focus().extendMarkRange('link').unsetLink()
					.run();
				return;
			}

			this.editor.chain().focus().extendMarkRange('link').setLink({ href: url })
				.run();
		},
		cleanWordHtml(html) {
			// Strip conditional comments
			let clean = html.replace(/<!--\[if[\s\S]*?\[endif\]-->/gi, '');
			// Remove <xml>...</xml> blocks
			clean = clean.replace(/<xml[\s\S]*?<\/xml>/gi, '');
			// Remove <style>...</style> blocks
			clean = clean.replace(/<style[\s\S]*?<\/style>/gi, '');
			// Remove <meta ...> tags
			clean = clean.replace(/<meta[^>]*>/gi, '');
			// Remove Office namespace tags like <o:p>, <w:...>, <m:...>
			clean = clean.replace(/<\/?[ow]:[^>]*>/gi, '');
			clean = clean.replace(/<\/?m:[^>]*>/gi, '');
			// Strip mso-* and Office class attributes but keep structural tags
			clean = clean.replace(/\s+style="[^"]*"/gi, (match) => {
				// Remove entirely if only mso styles, otherwise strip mso props
				const cleaned = match.replace(/mso-[^;:"]+;?\s*/gi, '').replace(/\s+style=""\s*/gi, '');
				return cleaned;
			});
			// Remove MsoNormal and similar class attributes
			clean = clean.replace(/\s+class="[^"]*Mso[^"]*"/gi, '');
			// Remove lang= attributes
			clean = clean.replace(/\s+lang="[^"]*"/gi, '');
			// Collapse multiple spaces
			clean = clean.replace(/&nbsp;&nbsp;+/g, ' ');
			// Remove empty paragraphs that Word adds
			clean = clean.replace(/<p[^>]*>\s*<\/p>/gi, '');

			return clean;
		},
		insertTemplate(type) {
			this.showTemplateMenu = false;
			const templates = {
				coupons: `
					<h3>C&oacute;mo canjear un c&oacute;digo descuento de _NOMBRE_TAG_</h3>
					<p>Descripci&oacute;n opcional</p>
					<ol>
						<li>Instrucci&oacute;n 1</li>
						<li>Instrucci&oacute;n 2</li>
						<li>Instrucci&oacute;n 3</li>
					</ol>
				`,
				faq: `
					<h3>FAQs: Preguntas frecuentes sobre _NOMBRE_TAG_</h3>
					<h4>Pregunta 1</h4>
					<p>Descripción 1</p>
					<h4>Pregunta 2</h4>
					<p>Descripción 2</p>
					<h4>Pregunta 3</h4>
					<p>Descripción 3</p>
				`,
				payments: `
					<h3>Diferentes m&eacute;todos de pago en _NOMBRE_TAG_</h3>
					<p>Descripci&oacute;n opcional</p>
					<ol>
						<li>Visa</li>
						<li>Mastercard</li>
						<li>Maestro</li>
						<li>American Express</li>
						<li>Tarjeta de regalo</li>
						<li>Otro pago 1</li>
						<li>Otro pago 2</li>
						<li>Otro pago 3</li>
					</ol>
				`,
			};

			if (templates[type]) {
				this.editor.chain().focus().insertContent(templates[type]).run();
			}
		},
		async runGenerate() {
			if (!this.generateHandler || this.generating) return;
			this.generating = true;
			try {
				const html = await this.generateHandler();
				if (html) {
					this.editor.chain().focus().insertContent(html).run();
				}
			} catch (e) {
				/* el componente padre gestiona los avisos de error */
			} finally {
				this.generating = false;
			}
		},
	},
};
</script>

<style lang="scss">
.tiptap-editor {
	border: 1px solid #d9d9d9;
	border-radius: 4px;
	display: flex;
	flex-direction: column;

	&.tiptap-fullscreen {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		z-index: 9999;
		background: #fff;
		border-radius: 0;

		.tiptap-content {
			height: calc(100vh - 90px) !important;
		}
	}

	.tiptap-toolbar {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		padding: 4px 8px;
		border-bottom: 1px solid #d9d9d9;
		background: #fafafa;
		gap: 2px;
	}

	.tiptap-toolbar-group {
		display: flex;
		align-items: center;
		gap: 1px;
	}

	.tiptap-toolbar-right {
		margin-left: auto;
	}

	.tiptap-separator {
		width: 1px;
		height: 24px;
		background: #d9d9d9;
		margin: 0 4px;
	}

	.tiptap-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 28px;
		height: 28px;
		padding: 2px 6px;
		border: 1px solid transparent;
		border-radius: 3px;
		background: transparent;
		cursor: pointer;
		font-size: 13px;
		color: #333;
		white-space: nowrap;

		&:hover {
			background: #e8e8e8;
		}

		&.is-active {
			background: #1890ff;
			color: #fff;
		}

		&:disabled {
			opacity: 0.4;
			cursor: not-allowed;
		}
	}

	.tiptap-btn-generate {
		background: #5b3cc4;
		color: #fff;
		font-weight: 600;
		padding: 2px 10px;

		&:hover {
			background: #4a30a3;
		}

		&:disabled {
			opacity: 0.6;
			cursor: wait;
		}
	}

	.tiptap-select {
		height: 28px;
		border: 1px solid #d9d9d9;
		border-radius: 3px;
		padding: 0 4px;
		font-size: 12px;
		background: #fff;
		cursor: pointer;
	}

	.tiptap-bubble {
		display: flex;
		gap: 2px;
		padding: 3px;
		background: #1f1f1f;
		border-radius: 6px;
		box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);

		.tiptap-btn {
			color: #fff;

			&:hover {
				background: rgba(255, 255, 255, 0.15);
			}

			&.is-active {
				background: #1890ff;
			}
		}
	}

	.tiptap-slash-menu {
		position: fixed;
		z-index: 10000;
		background: #fff;
		border: 1px solid #d9d9d9;
		border-radius: 6px;
		box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
		min-width: 220px;
		max-height: 280px;
		overflow-y: auto;
		padding: 4px;
	}

	.tiptap-slash-item {
		display: flex;
		align-items: center;
		gap: 8px;
		width: 100%;
		padding: 6px 10px;
		border: none;
		background: transparent;
		cursor: pointer;
		text-align: left;
		font-size: 13px;
		border-radius: 4px;

		&:hover,
		&.is-active {
			background: #f0f0f0;
		}
	}

	.tiptap-slash-icon {
		width: 20px;
		text-align: center;
		color: #666;
		flex-shrink: 0;
	}

	.tiptap-slash-empty {
		padding: 8px 10px;
		color: #999;
		font-size: 13px;
	}

	.tiptap-color-input {
		width: 28px;
		height: 28px;
		padding: 1px;
		border: 1px solid #d9d9d9;
		border-radius: 3px;
		cursor: pointer;
	}

	.tiptap-dropdown {
		position: relative;
	}

	.tiptap-dropdown-menu {
		position: absolute;
		top: 100%;
		left: 0;
		background: #fff;
		border: 1px solid #d9d9d9;
		border-radius: 4px;
		box-shadow: 0 2px 8px rgba(0,0,0,0.15);
		z-index: 200;
		min-width: 160px;
	}

	.tiptap-dropdown-item {
		display: block;
		width: 100%;
		padding: 8px 12px;
		border: none;
		background: transparent;
		cursor: pointer;
		text-align: left;
		font-size: 13px;

		&:hover {
			background: #f0f0f0;
		}
	}

	.tiptap-emoji-menu,
	.tiptap-charmap-menu {
		min-width: 280px;
		max-height: 320px;
		overflow-y: auto;
		padding: 8px;
	}

	.tiptap-emoji-group {
		margin-bottom: 10px;
	}

	.tiptap-emoji-group-label {
		font-size: 11px;
		font-weight: 600;
		color: #999;
		text-transform: uppercase;
		margin-bottom: 4px;
	}

	.tiptap-emoji-grid {
		display: flex;
		flex-wrap: wrap;
		gap: 2px;
	}

	.tiptap-emoji-btn {
		width: 28px;
		height: 28px;
		display: flex;
		align-items: center;
		justify-content: center;
		border: 1px solid transparent;
		border-radius: 3px;
		background: transparent;
		cursor: pointer;
		font-size: 16px;
		padding: 0;

		&:hover {
			background: #f0f0f0;
			border-color: #d9d9d9;
		}
	}

	.tiptap-content {
		overflow-y: auto;
		padding: 0;

		.ProseMirror {
			padding: 12px 16px;
			min-height: 100%;
			outline: none;

			> * + * {
				margin-top: 0.75em;
			}

			p.is-editor-empty:first-child::before {
				content: attr(data-placeholder);
				float: left;
				color: #adb5bd;
				pointer-events: none;
				height: 0;
			}

			h2, h3, h4, h5, h6 {
				line-height: 1.3;
				font-weight: 700;
			}

			h2 { font-size: 1.5em; }
			h3 { font-size: 1.3em; }
			h4 { font-size: 1.1em; }

			ul, ol {
				padding-left: 1.5em;
			}

			ul[data-type="taskList"] {
				list-style: none;
				padding-left: 0;

				li {
					display: flex;
					align-items: flex-start;
					gap: 0.5em;

					> label {
						margin: 0;
						user-select: none;
					}

					> div {
						flex: 1;
					}
				}
			}

			blockquote {
				border-left: 3px solid #d9d9d9;
				padding-left: 1em;
				color: #666;
			}

			img {
				max-width: 100%;
				height: auto;

				&.ProseMirror-selectednode {
					outline: 2px solid #1890ff;
				}
			}

			.tiptap-youtube {
				width: 100%;
				aspect-ratio: 16/9;
				border: none;
				border-radius: 4px;

				&.ProseMirror-selectednode {
					outline: 2px solid #1890ff;
				}
			}

			a {
				color: #1890ff;
				text-decoration: underline;
			}

			table {
				border-collapse: collapse;
				width: 100%;
				margin: 0;
				overflow: hidden;

				td, th {
					min-width: 1em;
					border: 1px solid #d9d9d9;
					padding: 6px 8px;
					vertical-align: top;
					box-sizing: border-box;
					position: relative;
				}

				th {
					font-weight: bold;
					background: #fafafa;
				}

				.selectedCell {
					background: #e6f7ff;
				}
			}

			code {
				background-color: rgba(#616161, 0.1);
				color: #616161;
				border-radius: 3px;
				padding: 0.2em 0.4em;
				font-size: 0.85em;
			}

			pre {
				background: #272822;
				color: #f8f8f2;
				padding: 0.75rem 1rem;
				border-radius: 4px;

				code {
					color: inherit;
					padding: 0;
					background: none;
					font-size: 0.8rem;
				}
			}

			hr {
				border: none;
				border-top: 1px solid #d9d9d9;
				margin: 1em 0;
			}
		}
	}

	.tiptap-statusbar {
		display: flex;
		align-items: center;
		padding: 4px 12px;
		border-top: 1px solid #d9d9d9;
		background: #fafafa;
		font-size: 12px;
		color: #999;
	}

	.tiptap-icon {
		font-size: 16px;
		line-height: 1;
	}
}
</style>
